import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { RewardType } from 'src/app/shared/models/constants/reward-type';
import { ActivityCardComponent } from '../activity-card/activity-card.component';
import { CouponCardService } from '../../../../core/services/coupon-card/coupon-card.service';
import { ProductService } from 'src/app/core/api/product/product.service';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Router } from '@angular/router';
import { CouponStatusInfo } from 'src/app/shared/models/model/couponStatusInfo/coupon-status-info';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';

@Component({
  selector: 'app-coupon-card',
  templateUrl: './coupon-card.component.html',
  styleUrls: ['./coupon-card.component.scss'],
})
/**
 * Instantiates warranty card as a coupon
 */
export class CouponCardComponent extends ActivityCardComponent {
  @Input() printerModel?: string;
  @Input() rewardName?: string;
  @Input() rewardDiscount?: number;
  @Input() discountValue?: number;
  @Input() description?: string;
  @Input() serial?: string;
  @Input() couponCode?: string;
  @Input() reclaimedDate!: string;
  @Input() cost?: number;
  @Input() rewardType?: RewardType;
  @Input() invoice?: string;
  @Input() resellerIdentification?: string;

  statusDisabled: boolean = false;
  loadingCouponInfo = false;
  language = '';
  showCouponStatus = false;
  couponData: any = {};
  override viewOnly!: boolean;
  isCallCenterAgent!: boolean;
  isRedeemableSuspendedCoupon = false;
  isProductDiscount = false;

  readonly couponReclaimedText = 'date-issued';
  readonly couponExpiringText = 'expiration-date';
  readonly changeCouponStatusText = 'change-coupon-status';
  readonly editRedeemDataText = 'edit-redeem-data';
  readonly printer = 'printer';
  readonly discountOn = 'discount-on';
  readonly serviceExtension = '1-year-costumer-service-extension';

  // prettier-ignore
  /**
   * @constructor
   * @param {ProductService} productService
   * @param {RewardService} rewardService
   * @param {TranslationService} translationService
   * @param {SnackBarService} snackbarService
   * @param {ChangeDetectorRef} changeDetector
   * @param {Router} router
   * @param {CouponCardService} couponCardService
   */
  constructor( // NOSONAR
    productService: ProductService,
    rewardService: RewardService,
    translationService: TranslationService,
    snackbarService: SnackBarService,
    changeDetector: ChangeDetectorRef,
    router: Router,
    couponCardService: CouponCardService,
    private authenticatedUserService: AuthenticatedUserService
  ) {
    super(
      rewardService,
      productService,
      translationService,
      snackbarService,
      changeDetector,
      router,
      couponCardService
    );
  }

  // do not run on init of activity card because it will already run when instantiated in template
  // eslint-disable-next-line
  override ngOnInit() {
    this.language = this.translationService.getActiveLang();
    this.couponData = {
      printerModel: this.printerModel,
      rewardName: this.rewardName,
      rewardDiscount: this.rewardDiscount,
      description: this.description,
      serial: this.serial,
      couponCode: this.couponCode,
      reclaimedDate: this.reclaimedDate,
      cost: this.cost,
      expirationDate: this.expirationDate,
      statusCheck: this.statusCheck,
      status: this.couponStatus,
      couponId: this.id,
      activePoints: undefined,
      expiredPoints: undefined,
      resellerIdentification: this.resellerIdentification,
      invoice: this.invoice,
      discountValue: this.discountValue,
    };
    if (this.isSuspended) {
      this.isRedeemableSuspendedCoupon = this.isAbleToRedeemSuspended();
    }
    this.statusDisabled = this.isDisableButton();
    this.bootCouponStatus();
    this.viewOnly = this.authenticatedUserService.isVOAdmin();
    this.isCallCenterAgent = this.authenticatedUserService.isCallCenterAgent();
    this.isProductDiscount = this.rewardType === RewardType.PRODUCT_DISCOUNT;
  }

  /**
   * Open coupon status if it was opened before
   */
  private bootCouponStatus() {
    this.couponCardService.couponStatuses.forEach((couponStatus) => {
      if (couponStatus.couponId === this.id) {
        this.getCouponsPoints();
        this.changeCouponStatus();
      }
    });
  }

  /**
   * Call API and get total and used poins in coupon
   */
  private getCouponsPoints() {
    if (!this.loadingCouponInfo) {
      this.rewardService
        .getTotalPointsUsedInCoupon(this.couponData.couponId)
        .subscribe((coupon) => {
          this.couponData.activePoints = coupon.activePoints;
          this.couponData.expiredPoints = coupon.expiredPoints;
        });
      this.loadingCouponInfo = true;
    }
  }

  /**
   * Change coupon status based on previous one
   */
  private changeCouponStatus() {
    if (!this.statusDisabled) {
      this.showCouponStatus = !this.showCouponStatus;
    }
  }

  /**
   * toggleCouponStatus
   */
  toggleCouponStatus() {
    this.setPointsInCoupon();
    this.changeCouponStatus();
  }

  /**
   * Get points data from the selected coupon
   */
  private setPointsInCoupon() {
    this.couponCardService.couponStatuses.push(
      new CouponStatusInfo(this.couponData.couponId, '')
    );
    this.getCouponsPoints();
  }

  /**
   * closeCouponStatus
   */
  closeCouponStatus() {
    this.showCouponStatus = false;
    this.couponCardService.removeCouponStatusById(this.couponData.couponId);
  }

  /**
   * Update coupon data
   * @param {any} coupon
   */
  updateCouponData(coupon: any) {
    this.couponData = {
      printerModel: coupon.printerModel,
      rewardName: coupon.rewardName,
      rewardDiscount: coupon.rewardDiscount,
      description: coupon.description,
      serial: coupon.serial,
      couponCode: coupon.couponCode,
      reclaimedDate: coupon.reclaimedDate,
      cost: coupon.cost,
      expirationDate: coupon.expirationDate,
      statusCheck: coupon.statusCheck,
      status: coupon.couponStatus,
      couponId: coupon.couponId,
    };
    this.couponStatus = coupon.couponStatus;
    this.usedDate = this.translationService.localizeDate(coupon.usedDate);
    this.showCouponStatus = false;
    this.statusDisabled = this.isDisableButton();
    this.couponCardService.removeCouponStatusById(coupon.couponId);
    this.changeDetector.detectChanges();
  }

  /**
   *
   * @return {boolean}
   */
  private isAbleToRedeemSuspended(): boolean {
    const disabledDate = new Date(this.disabledAt!);
    const currentDate = new Date();

    const differenceMs = currentDate.getTime() - disabledDate.getTime();

    const differenceDays = differenceMs / (1000 * 60 * 60 * 24);

    return differenceDays <= 60;
  }

  /**
   * Defines the button as enabled or disabled
   * @return {boolean}
   */
  isDisableButton(): boolean {
    const userRole = this.authenticatedUserService.admin?.role;
    const status = this.couponData.status;

    if (userRole === 'VIEW_ONLY' || status === CouponStatus.CANCELED) {
      return true;
    }

    if (this.isSuspended) {
      return !this.isRedeemableSuspendedCoupon;
    }

    if (userRole !== 'GLOBAL_ADMIN') {
      return status !== CouponStatus.ACTIVE && status !== CouponStatus.REDEEMED;
    }

    return false;
  }
}
